import { useCallback, useEffect, useRef, useState } from "react";
import { TextInput } from "flowbite-react";
import { CustomButton } from "./CustomButton";
import {
  BiBook,
  BiBuilding,
  BiCalendar,
  BiCamera,
  BiCar,
  BiCart,
  BiCheck,
  BiCloudDownload,
  BiCoffee,
  BiCog,
  BiCut,
  BiDownload,
  BiDumbbell,
  BiEnvelope,
  BiGlobe,
  BiHealth,
  BiHeart,
  BiHome,
  BiImage,
  BiLaptop,
  BiMap,
  BiMessageDetail,
  BiMoney,
  BiMusic,
  BiNews,
  BiPalette,
  BiPhone,
  BiPrinter,
  BiRestaurant,
  BiShield,
  BiStar,
  BiSolidStar,
  BiSupport,
  BiTime,
  BiTrophy,
  BiUser,
  BiWifi,
  BiWrench,
  BiX,
  BiVideo,
  BiGift,
  BiSolidPizza,
  BiDrink,
  BiBriefcase,
  BiPackage,
  BiLeaf,
  BiLock,
  BiPlay,
  BiMicrophone,
  BiHeadphone,
  BiMoviePlay,
  BiCreditCard,
  BiWallet,
  BiBarChart,
  BiPieChart,
  BiLineChart,
  BiFootball,
  BiRun,
  BiPencil,
  BiBrush,
  BiRocket,
  BiFirstAid,
  BiClinic,
  BiCodeAlt,
  BiTerminal,
  BiServer,
  BiMobileAlt,
  BiDesktop,
  BiBell,
  BiAlarm,
  BiSun,
  BiMoon,
  BiLogoVenmo,
} from "react-icons/bi";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTelegram,
  FaTiktok,
  FaTwitter,
  FaWhatsapp,
  FaYoutube,
  FaSnapchat,
  FaDiscord,
  FaPinterest,
  FaReddit,
  FaSignal,
  FaSpotify,
  FaApple,
  FaGooglePlay,
  FaTwitch,
  FaMedium,
  FaPatreon,
  FaSlack,
  FaGithub,
  FaGitlab,
  FaBitbucket,
  FaStackOverflow,
  FaDribbble,
  FaBehance,
  FaFigma,
  FaSketch,
  FaAmazon,
  FaEbay,
  FaEtsy,
  FaShopify,
  FaPaypal,
  FaStripe,
} from "react-icons/fa6";
import { BsShop } from "react-icons/bs";
import {
  SiNotion,
  SiTrello,
  SiAsana,
  SiMicrosoft,
  SiGoogle,
  SiZoom,
  SiSkype,
  SiDropbox,
  SiNetflix,
  SiPrime,
  SiHulu,
  SiHbo,
  SiSoundcloud,
  SiApplemusic,
  SiDeezer,
  SiPandora,
  SiAdobe,
  SiCashapp,
  SiGoogledrive,
  SiGooglemaps,
  SiWaze,
} from "react-icons/si";
import { useTranslation } from "react-i18next";
import { CiPill } from "react-icons/ci";
import { FaGamepad } from "react-icons/fa";
import { TbBrandDisney, TbBrandOnedrive } from "react-icons/tb";

export interface SuggestedIcon {
  id: string;
  name: string;
  icon: React.ReactNode;
  category?: string;
}

export interface IconPickerProps {
  selectedIcon?: string;
  onIconSelect: (iconId: string | null) => void;
  customIcons?: SuggestedIcon[];
  showCustomUrlInput?: boolean;
  placeholder?: string;
  disabled?: boolean;
  translations?: {
    selectIcon?: string;
    noIcon?: string;
    customUrl?: string;
    customUrlPlaceholder?: string;
    suggested?: string;
    social?: string;
    business?: string;
    general?: string;
  };
}

const defaultIcons: SuggestedIcon[] = [
  {
    id: "instagram",
    name: "Instagram",
    icon: <FaInstagram size={24} className="text-purple-500" />,
    category: "social",
  },
  {
    id: "facebook",
    name: "Facebook",
    icon: <FaFacebook size={24} className="text-blue-600" />,
    category: "social",
  },
  {
    id: "twitter",
    name: "Twitter/X",
    icon: <FaTwitter size={24} className="text-black" />,
    category: "social",
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    icon: <FaLinkedin size={24} className="text-blue-700" />,
    category: "social",
  },
  {
    id: "youtube",
    name: "YouTube",
    icon: <FaYoutube size={24} className="text-red-600" />,
    category: "social",
  },
  {
    id: "tiktok",
    name: "TikTok",
    icon: <FaTiktok size={24} className="text-gray-800" />,
    category: "social",
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    icon: <FaWhatsapp size={24} className="text-green-500" />,
    category: "social",
  },
  {
    id: "telegram",
    name: "Telegram",
    icon: <FaTelegram size={24} className="text-blue-500" />,
    category: "social",
  },
  {
    id: "snapchat",
    name: "Snapchat",
    icon: <FaSnapchat size={24} className="text-amber-400" />,
    category: "social",
  },
  {
    id: "discord",
    name: "Discord",
    icon: <FaDiscord size={24} className="text-blue-500" />,
    category: "social",
  },
  {
    id: "pinterest",
    name: "Pinterest",
    icon: <FaPinterest size={24} className="text-red-500" />,
    category: "social",
  },
  {
    id: "reddit",
    name: "Reddit",
    icon: <FaReddit size={24} className="text-amber-500" />,
    category: "social",
  },
  {
    id: "signal",
    name: "Signal",
    icon: <FaSignal size={24} className="text-blue-500" />,
    category: "social",
  },
  {
    id: "spotify",
    name: "Spotify",
    icon: <FaSpotify size={24} className="text-green-500" />,
    category: "social",
  },
  {
    id: "twitch",
    name: "Twitch",
    icon: <FaTwitch size={24} className="text-purple-600" />,
    category: "social",
  },
  {
    id: "medium",
    name: "Medium",
    icon: <FaMedium size={24} className="text-gray-800" />,
    category: "social",
  },
  {
    id: "patreon",
    name: "Patreon",
    icon: <FaPatreon size={24} className="text-amber-600" />,
    category: "social",
  },
  {
    id: "slack",
    name: "Slack",
    icon: <FaSlack size={24} className="text-purple-500" />,
    category: "social",
  },
  {
    id: "github",
    name: "GitHub",
    icon: <FaGithub size={24} className="text-gray-800" />,
    category: "social",
  },
  {
    id: "gitlab",
    name: "GitLab",
    icon: <FaGitlab size={24} className="text-amber-600" />,
    category: "social",
  },
  {
    id: "bitbucket",
    name: "Bitbucket",
    icon: <FaBitbucket size={24} className="text-blue-600" />,
    category: "social",
  },
  {
    id: "stackoverflow",
    name: "Stack Overflow",
    icon: <FaStackOverflow size={24} className="text-amber-500" />,
    category: "social",
  },
  {
    id: "dribbble",
    name: "Dribbble",
    icon: <FaDribbble size={24} className="text-purple-500" />,
    category: "social",
  },
  {
    id: "behance",
    name: "Behance",
    icon: <FaBehance size={24} className="text-blue-600" />,
    category: "social",
  },

  {
    id: "shop",
    name: "Shop",
    icon: <BsShop size={24} className="text-purple-500" />,
    category: "business",
  },
  {
    id: "cart",
    name: "Shopping",
    icon: <BiCart size={24} className="text-amber-500" />,
    category: "business",
  },
  {
    id: "coffee",
    name: "Café",
    icon: <BiCoffee size={24} className="text-amber-600" />,
    category: "business",
  },
  {
    id: "restaurant",
    name: "Restaurant",
    icon: <BiRestaurant size={24} className="text-red-500" />,
    category: "business",
  },
  {
    id: "car",
    name: "Automotive",
    icon: <BiCar size={24} className="text-gray-600" />,
    category: "business",
  },
  {
    id: "home",
    name: "Real Estate",
    icon: <BiHome size={24} className="text-green-600" />,
    category: "business",
  },
  {
    id: "building",
    name: "Office",
    icon: <BiBuilding size={24} className="text-blue-600" />,
    category: "business",
  },
  {
    id: "palette",
    name: "Design",
    icon: <BiPalette size={24} className="text-purple-500" />,
    category: "business",
  },
  {
    id: "cut",
    name: "Salon",
    icon: <BiCut size={24} className="text-purple-600" />,
    category: "business",
  },
  {
    id: "dumbbell",
    name: "Fitness",
    icon: <BiDumbbell size={24} className="text-red-600" />,
    category: "business",
  },
  {
    id: "health",
    name: "Healthcare",
    icon: <BiHealth size={24} className="text-green-500" />,
    category: "business",
  },
  {
    id: "wrench",
    name: "Repair",
    icon: <BiWrench size={24} className="text-gray-700" />,
    category: "business",
  },
  {
    id: "money",
    name: "Finance",
    icon: <BiMoney size={24} className="text-green-700" />,
    category: "business",
  },
  {
    id: "trophy",
    name: "Awards",
    icon: <BiTrophy size={24} className="text-amber-500" />,
    category: "business",
  },
  {
    id: "pizza",
    name: "Pizza",
    icon: <BiSolidPizza size={24} className="text-amber-600" />,
    category: "business",
  },
  {
    id: "drink",
    name: "Bar/Drinks",
    icon: <BiDrink size={24} className="text-blue-500" />,
    category: "business",
  },
  {
    id: "gift",
    name: "Gifts",
    icon: <BiGift size={24} className="text-red-500" />,
    category: "business",
  },
  {
    id: "briefcase",
    name: "Business",
    icon: <BiBriefcase size={24} className="text-gray-700" />,
    category: "business",
  },
  {
    id: "package",
    name: "Package/Delivery",
    icon: <BiPackage size={24} className="text-amber-600" />,
    category: "business",
  },
  {
    id: "leaf",
    name: "Eco/Nature",
    icon: <BiLeaf size={24} className="text-green-600" />,
    category: "business",
  },
  {
    id: "firstaid",
    name: "First Aid",
    icon: <BiFirstAid size={24} className="text-red-600" />,
    category: "business",
  },
  {
    id: "clinic",
    name: "Clinic",
    icon: <BiClinic size={24} className="text-blue-600" />,
    category: "business",
  },
  {
    id: "pill",
    name: "Pharmacy",
    icon: <CiPill size={24} className="text-purple-500" />,
    category: "business",
  },
  {
    id: "creditcard",
    name: "Credit Card",
    icon: <BiCreditCard size={24} className="text-blue-700" />,
    category: "business",
  },
  {
    id: "wallet",
    name: "Wallet",
    icon: <BiWallet size={24} className="text-gray-600" />,
    category: "business",
  },
  {
    id: "barchart",
    name: "Bar Chart",
    icon: <BiBarChart size={24} className="text-blue-600" />,
    category: "business",
  },
  {
    id: "piechart",
    name: "Pie Chart",
    icon: <BiPieChart size={24} className="text-green-600" />,
    category: "business",
  },
  {
    id: "linechart",
    name: "Line Chart",
    icon: <BiLineChart size={24} className="text-purple-600" />,
    category: "business",
  },
  {
    id: "figma",
    name: "Figma",
    icon: <FaFigma size={24} className="text-purple-500" />,
    category: "business",
  },
  {
    id: "sketch",
    name: "Sketch",
    icon: <FaSketch size={24} className="text-amber-500" />,
    category: "business",
  },
  {
    id: "adobe",
    name: "Adobe",
    icon: <SiAdobe size={24} className="text-red-600" />,
    category: "business",
  },
  {
    id: "amazon",
    name: "Amazon",
    icon: <FaAmazon size={24} className="text-amber-500" />,
    category: "business",
  },
  {
    id: "ebay",
    name: "eBay",
    icon: <FaEbay size={24} className="text-blue-600" />,
    category: "business",
  },
  {
    id: "etsy",
    name: "Etsy",
    icon: <FaEtsy size={24} className="text-amber-600" />,
    category: "business",
  },
  {
    id: "shopify",
    name: "Shopify",
    icon: <FaShopify size={24} className="text-green-600" />,
    category: "business",
  },
  {
    id: "paypal",
    name: "PayPal",
    icon: <FaPaypal size={24} className="text-blue-600" />,
    category: "business",
  },
  {
    id: "stripe",
    name: "Stripe",
    icon: <FaStripe size={24} className="text-purple-600" />,
    category: "business",
  },
  {
    id: "venmo",
    name: "Venmo",
    icon: <BiLogoVenmo size={24} className="text-blue-500" />,
    category: "business",
  },
  {
    id: "cashapp",
    name: "Cash App",
    icon: <SiCashapp size={24} className="text-green-500" />,
    category: "business",
  },

  {
    id: "sms",
    name: "SMS",
    icon: <BiMessageDetail size={24} className="text-green-500" />,
    category: "general",
  },

  {
    id: "googlemaps",
    name: "Google Maps",
    icon: <SiGooglemaps size={24} className="text-green-600" />,
    category: "general",
  },
  {
    id: "waze",
    name: "Waze",
    icon: <SiWaze size={24} className="text-blue-500" />,
    category: "general",
  },

  {
    id: "website",
    name: "Website",
    icon: <BiGlobe size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "phone",
    name: "Phone Call",
    icon: <BiPhone size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "email",
    name: "Email",
    icon: <BiEnvelope size={24} className="text-red-500" />,
    category: "general",
  },
  {
    id: "location",
    name: "Location",
    icon: <BiMap size={24} className="text-red-500" />,
    category: "general",
  },
  {
    id: "calendar",
    name: "Calendar",
    icon: <BiCalendar size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "star",
    name: "Favorite",
    icon: <BiStar size={24} className="text-amber-400" />,
    category: "general",
  },
  {
    id: "starfilled",
    name: "Favorite (Filled)",
    icon: <BiSolidStar size={24} className="text-amber-400" />,
    category: "general",
  },
  {
    id: "heart",
    name: "Love",
    icon: <BiHeart size={24} className="text-red-500" />,
    category: "general",
  },
  {
    id: "time",
    name: "Schedule",
    icon: <BiTime size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "user",
    name: "Profile",
    icon: <BiUser size={24} className="text-gray-600" />,
    category: "general",
  },
  {
    id: "book",
    name: "Education",
    icon: <BiBook size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "music",
    name: "Music",
    icon: <BiMusic size={24} className="text-purple-500" />,
    category: "general",
  },
  {
    id: "camera",
    name: "Photography",
    icon: <BiCamera size={24} className="text-gray-700" />,
    category: "general",
  },
  {
    id: "support",
    name: "Support",
    icon: <BiSupport size={24} className="text-green-600" />,
    category: "general",
  },
  {
    id: "download",
    name: "Download",
    icon: <BiDownload size={24} className="text-blue-700" />,
    category: "general",
  },
  {
    id: "cloud",
    name: "Cloud",
    icon: <BiCloudDownload size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "shield",
    name: "Security",
    icon: <BiShield size={24} className="text-green-700" />,
    category: "general",
  },
  {
    id: "settings",
    name: "Settings",
    icon: <BiCog size={24} className="text-gray-600" />,
    category: "general",
  },
  {
    id: "news",
    name: "News",
    icon: <BiNews size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "laptop",
    name: "Tech",
    icon: <BiLaptop size={24} className="text-gray-700" />,
    category: "general",
  },
  {
    id: "wifi",
    name: "WiFi",
    icon: <BiWifi size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "print",
    name: "Print",
    icon: <BiPrinter size={24} className="text-gray-600" />,
    category: "general",
  },
  {
    id: "video",
    name: "Video",
    icon: <BiVideo size={24} className="text-purple-600" />,
    category: "general",
  },
  {
    id: "lock",
    name: "Lock/Privacy",
    icon: <BiLock size={24} className="text-gray-700" />,
    category: "general",
  },
  {
    id: "play",
    name: "Play",
    icon: <BiPlay size={24} className="text-green-500" />,
    category: "general",
  },
  {
    id: "microphone",
    name: "Microphone/Podcast",
    icon: <BiMicrophone size={24} className="text-red-500" />,
    category: "general",
  },
  {
    id: "headphone",
    name: "Headphones/Audio",
    icon: <BiHeadphone size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "movie",
    name: "Movie/Film",
    icon: <BiMoviePlay size={24} className="text-purple-700" />,
    category: "general",
  },
  {
    id: "gamepad",
    name: "Gaming",
    icon: <FaGamepad size={24} className="text-purple-600" />,
    category: "general",
  },
  {
    id: "football",
    name: "Football/Sports",
    icon: <BiFootball size={24} className="text-green-700" />,
    category: "general",
  },
  {
    id: "run",
    name: "Running",
    icon: <BiRun size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "pencil",
    name: "Pencil/Write",
    icon: <BiPencil size={24} className="text-gray-700" />,
    category: "general",
  },
  {
    id: "brush",
    name: "Brush/Art",
    icon: <BiBrush size={24} className="text-purple-600" />,
    category: "general",
  },
  {
    id: "rocket",
    name: "Rocket/Startup",
    icon: <BiRocket size={24} className="text-red-600" />,
    category: "general",
  },
  {
    id: "code",
    name: "Code/Development",
    icon: <BiCodeAlt size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "terminal",
    name: "Terminal",
    icon: <BiTerminal size={24} className="text-gray-800" />,
    category: "general",
  },
  {
    id: "server",
    name: "Server",
    icon: <BiServer size={24} className="text-gray-700" />,
    category: "general",
  },
  {
    id: "mobile",
    name: "Mobile",
    icon: <BiMobileAlt size={24} className="text-gray-700" />,
    category: "general",
  },
  {
    id: "desktop",
    name: "Desktop",
    icon: <BiDesktop size={24} className="text-gray-700" />,
    category: "general",
  },
  {
    id: "bell",
    name: "Notifications",
    icon: <BiBell size={24} className="text-amber-500" />,
    category: "general",
  },
  {
    id: "alarm",
    name: "Alarm",
    icon: <BiAlarm size={24} className="text-red-500" />,
    category: "general",
  },
  {
    id: "sun",
    name: "Day/Light",
    icon: <BiSun size={24} className="text-amber-500" />,
    category: "general",
  },
  {
    id: "moon",
    name: "Night/Dark",
    icon: <BiMoon size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "appstore",
    name: "App Store",
    icon: <FaApple size={24} className="text-gray-800" />,
    category: "general",
  },
  {
    id: "googleplay",
    name: "Google Play",
    icon: <FaGooglePlay size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "notion",
    name: "Notion",
    icon: <SiNotion size={24} className="text-gray-800" />,
    category: "general",
  },
  {
    id: "trello",
    name: "Trello",
    icon: <SiTrello size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "asana",
    name: "Asana",
    icon: <SiAsana size={24} className="text-red-500" />,
    category: "general",
  },
  {
    id: "microsoft",
    name: "Microsoft",
    icon: <SiMicrosoft size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "google",
    name: "Google",
    icon: <SiGoogle size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "zoom",
    name: "Zoom",
    icon: <SiZoom size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "skype",
    name: "Skype",
    icon: <SiSkype size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "dropbox",
    name: "Dropbox",
    icon: <SiDropbox size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "onedrive",
    name: "OneDrive",
    icon: <TbBrandOnedrive size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "googledrive",
    name: "Google Drive",
    icon: <SiGoogledrive size={24} className="text-amber-500" />,
    category: "general",
  },
  {
    id: "netflix",
    name: "Netflix",
    icon: <SiNetflix size={24} className="text-red-600" />,
    category: "general",
  },
  {
    id: "prime",
    name: "Prime Video",
    icon: <SiPrime size={24} className="text-blue-500" />,
    category: "general",
  },
  {
    id: "hulu",
    name: "Hulu",
    icon: <SiHulu size={24} className="text-green-500" />,
    category: "general",
  },
  {
    id: "disney",
    name: "Disney+",
    icon: <TbBrandDisney size={24} className="text-blue-600" />,
    category: "general",
  },
  {
    id: "hbo",
    name: "HBO",
    icon: <SiHbo size={24} className="text-purple-600" />,
    category: "general",
  },
  {
    id: "soundcloud",
    name: "SoundCloud",
    icon: <SiSoundcloud size={24} className="text-amber-500" />,
    category: "general",
  },
  {
    id: "applemusic",
    name: "Apple Music",
    icon: <SiApplemusic size={24} className="text-red-500" />,
    category: "general",
  },
  {
    id: "deezer",
    name: "Deezer",
    icon: <SiDeezer size={24} className="text-purple-500" />,
    category: "general",
  },
  {
    id: "pandora",
    name: "Pandora",
    icon: <SiPandora size={24} className="text-blue-600" />,
    category: "general",
  },
];

export const IconPicker = ({
  selectedIcon,
  onIconSelect,
  customIcons = [],
  showCustomUrlInput = false,
  placeholder = "Select an icon",
  disabled = false,
}: IconPickerProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [activeTab, setActiveTab] = useState<string>("social");
  const containerRef = useRef<HTMLDivElement>(null);

  const allIcons = [...defaultIcons, ...customIcons];

  const categorizedIcons = {
    social: allIcons.filter((icon) => icon.category === "social"),
    business: allIcons.filter((icon) => icon.category === "business"),
    general: allIcons.filter((icon) => icon.category === "general"),
  };

  const selectedIconData = allIcons.find((icon) => icon.id === selectedIcon);

  const handleIconSelect = useCallback(
    (iconId: string | null) => {
      onIconSelect(iconId);
      setIsOpen(false);
    },
    [onIconSelect]
  );

  const handleCustomUrlSubmit = useCallback(() => {
    if (customUrl.trim()) {
      onIconSelect(customUrl.trim());
      setCustomUrl("");
      setIsOpen(false);
    }
  }, [customUrl, onIconSelect]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full h-[42px] px-3 border-1 border-dashed rounded-lg flex items-center justify-center transition-all relative ${
          disabled ? "" : "hover:border-blue-400 hover:bg-blue-50"
        } ${
          selectedIcon
            ? "border-green-300 bg-green-50"
            : "border-gray-300 bg-gray-50"
        }`}
      >
        {selectedIcon && !disabled && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleIconSelect(null);
            }}
            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors cursor-pointer"
            title="Remove icon"
          >
            <BiX size={10} />
          </div>
        )}

        {selectedIconData ? (
          <div className="flex items-center justify-center">
            <div className="flex-shrink-0">{selectedIconData.icon}</div>
          </div>
        ) : selectedIcon && selectedIcon.startsWith("http") ? (
          <div className="flex items-center gap-2">
            <img
              src={selectedIcon}
              alt="Custom icon"
              className="w-6 h-6 object-contain flex-shrink-0"
            />
            <span className="text-sm font-medium text-gray-800">
              {t("dashboard.features.lmc.redirectLinks.custom")}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <BiImage size={20} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
              {t("dashboard.features.lmc.redirectLinks.addIcon")}
            </span>
          </div>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-80 z-50  border-1 border-gray-400 rounded-lg shadow-lg mt-1 backdrop-blur-sm ">
          <div className="border-1 border-gray-300 bg-gray-100 rounded-t-lg">
            <div className="flex text-xs">
              {[
                { key: "social", label: "📱", tooltip: "Social" },
                { key: "business", label: "🏢", tooltip: "Business" },
                { key: "general", label: "🌐", tooltip: "General" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 py-2 px-3 font-medium transition-colors first:rounded-tl-lg border-r border-gray-300 last:border-r-0 ${
                    activeTab === tab.key
                      ? "bg-white border-b-2 border-blue-500 text-blue-600 shadow-sm"
                      : "text-gray-700 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                  title={tab.tooltip}
                >
                  {tab.label}
                </button>
              ))}
              {showCustomUrlInput && (
                <button
                  type="button"
                  onClick={() => setActiveTab("custom")}
                  className={`flex-1 py-2 px-3 font-medium transition-colors border-r border-gray-300 last:border-r-0 ${
                    activeTab === "custom"
                      ? "bg-white border-b-2 border-blue-500 text-blue-600 shadow-sm"
                      : "text-gray-700 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                  title="Custom URL"
                >
                  🔗
                </button>
              )}
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto bg-white rounded-b-lg border border-gray-200">
            {activeTab === "custom" ? (
              <div className="p-3">
                <div className="flex gap-2">
                  <TextInput
                    type="url"
                    sizing="sm"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    placeholder="Icon URL"
                    className="flex-1"
                  />
                  <CustomButton
                    variant="primary"
                    size="xs"
                    onClick={handleCustomUrlSubmit}
                    disabled={!customUrl.trim()}
                    comp={<BiCheck size={16} />}
                  />
                </div>
              </div>
            ) : (
              <div className="p-2">
                <button
                  type="button"
                  onClick={() => handleIconSelect(null)}
                  className={`w-full p-2 hover:bg-gray-50 flex items-center gap-2 rounded transition-colors text-left ${
                    !selectedIcon ? "bg-blue-50 text-blue-600" : ""
                  }`}
                >
                  <div className="w-5 h-5 border border-dashed border-gray-300 rounded flex items-center justify-center flex-shrink-0">
                    <BiX size={12} className="text-gray-400" />
                  </div>
                  <span className="text-sm">No Icon</span>
                  {!selectedIcon && (
                    <BiCheck size={14} className="text-blue-500 ml-auto" />
                  )}
                </button>

                <div className="grid grid-cols-6 gap-1 mt-2 p-2 border border-gray-300 rounded-lg bg-gray-50">
                  {categorizedIcons[
                    activeTab as keyof typeof categorizedIcons
                  ]?.map((iconData) => (
                    <button
                      key={iconData.id}
                      type="button"
                      onClick={() => handleIconSelect(iconData.id)}
                      className={`p-2 rounded border transition-colors flex items-center justify-center relative ${
                        selectedIcon === iconData.id
                          ? "bg-blue-50 border-blue-400 ring-1 ring-blue-400"
                          : "border-transparent hover:bg-white hover:border-gray-300"
                      }`}
                      title={iconData.name}
                    >
                      <div className="text-xl">{iconData.icon}</div>
                      {selectedIcon === iconData.id && (
                        <BiCheck
                          size={12}
                          className="absolute top-0 right-0 text-blue-500 bg-white rounded-full"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
